import React, { useState, createContext, useContext } from 'react';
import { availableTags as initialAvailableTags } from '../data/mockSubscriptionData';
export interface TagData {
  id: string;
  label: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'gray' | string;
}
export interface CustomerTags {
  customerId: string;
  tags: TagData[];
}
interface TagsContextType {
  // Global tag management
  allTags: TagData[];
  createTag: (tag: TagData) => void;
  updateTag: (tagId: string, updates: Partial<TagData>) => void;
  deleteTag: (tagId: string) => void;
  // Customer-specific tag management
  getCustomerTags: (customerId: string) => TagData[];
  addTagToCustomer: (customerId: string, tag: TagData) => void;
  removeTagFromCustomer: (customerId: string, tagId: string) => void;
}
const TagsContext = createContext<TagsContextType | undefined>(undefined);
// Initial customer tags (mock data)
const initialCustomerTags: CustomerTags[] = [{
  customerId: '1',
  tags: [{
    id: 'vip',
    label: 'VIP',
    color: 'purple'
  }, {
    id: 'premium',
    label: 'Premium',
    color: 'purple'
  }]
}];
export function TagsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [allTags, setAllTags] = useState<TagData[]>(initialAvailableTags);
  const [customerTags, setCustomerTags] = useState<CustomerTags[]>(initialCustomerTags);
  // Global tag management
  const createTag = (tag: TagData) => {
    // Add to global tags if it doesn't exist
    if (!allTags.find(t => t.id === tag.id)) {
      setAllTags(prev => [...prev, tag]);
    }
  };
  const updateTag = (tagId: string, updates: Partial<TagData>) => {
    setAllTags(prev => prev.map(tag => tag.id === tagId ? {
      ...tag,
      ...updates
    } : tag));
    // Also update in customer tags
    setCustomerTags(prev => prev.map(ct => ({
      ...ct,
      tags: ct.tags.map(tag => tag.id === tagId ? {
        ...tag,
        ...updates
      } : tag)
    })));
  };
  const deleteTag = (tagId: string) => {
    setAllTags(prev => prev.filter(tag => tag.id !== tagId));
    // Remove from all customers
    setCustomerTags(prev => prev.map(ct => ({
      ...ct,
      tags: ct.tags.filter(tag => tag.id !== tagId)
    })));
  };
  // Customer-specific tag management
  const getCustomerTags = (customerId: string): TagData[] => {
    const customer = customerTags.find(ct => ct.customerId === customerId);
    return customer?.tags || [];
  };
  const addTagToCustomer = (customerId: string, tag: TagData) => {
    // Ensure tag exists in global tags
    if (!allTags.find(t => t.id === tag.id)) {
      createTag(tag);
    }
    setCustomerTags(prev => {
      const existingCustomer = prev.find(ct => ct.customerId === customerId);
      if (existingCustomer) {
        // Customer exists, add tag if not already present
        if (!existingCustomer.tags.find(t => t.id === tag.id)) {
          return prev.map(ct => ct.customerId === customerId ? {
            ...ct,
            tags: [...ct.tags, tag]
          } : ct);
        }
        return prev;
      } else {
        // Customer doesn't exist, create new entry
        return [...prev, {
          customerId,
          tags: [tag]
        }];
      }
    });
  };
  const removeTagFromCustomer = (customerId: string, tagId: string) => {
    setCustomerTags(prev => prev.map(ct => ct.customerId === customerId ? {
      ...ct,
      tags: ct.tags.filter(t => t.id !== tagId)
    } : ct));
  };
  return <TagsContext.Provider value={{
    allTags,
    createTag,
    updateTag,
    deleteTag,
    getCustomerTags,
    addTagToCustomer,
    removeTagFromCustomer
  }} data-id="element-6356" data-mp="28jfmolwprgcd">
      {children}
    </TagsContext.Provider>;
}
export function useTags() {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
}